import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { UserRole, OrganizationUser } from '@/types/users'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'

type EditUserModalProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: (userId: string, role: UserRole) => Promise<void>
  selectedUser: OrganizationUser | null
}

const EditUserModal = ({
  isOpen,
  onClose,
  onSubmit,
  selectedUser,
}: EditUserModalProps) => {
  const { t } = useTranslation()
  const [selectedRole, setSelectedRole] = useState<UserRole>('member')
  const [isLoading, setIsLoading] = useState(false)

  const roleOptions = [
    { value: 'admin' as UserRole, label: t('common:team_modals.roles.admin') },
    { value: 'annotator' as UserRole, label: t('common:team_modals.roles.annotator') },
    { value: 'member' as UserRole, label: t('common:team_modals.roles.member') },
  ]

  useEffect(() => {
    if (selectedUser) {
      setSelectedRole(selectedUser.role)
    }
  }, [selectedUser])

  const handleCloseModal = () => {
    onClose()
    setSelectedRole('member')
  }

  const handleSubmit = async () => {
    if (!selectedUser?.user_id) {
      toast.error(t('common:team_modals.edit.user_not_found'))
      return
    }

    setIsLoading(true)
    try {
      await onSubmit(selectedUser.user_id, selectedRole)
      handleCloseModal()
    } catch {
      // Error handling is done in the parent component
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      description={t('common:team_modals.edit.description')}
      title={t('common:team_modals.edit.title')}
      className="w-[400px]"
      footer={
        <>
          <Button variant="outline" onClick={handleCloseModal} disabled={isLoading}>
            {t('common:team_modals.edit.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? t('common:team_modals.edit.updating') : t('common:team_modals.edit.update')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 py-4">
        <div className="grid gap-2">
          <Label htmlFor="edit-role">{t('common:team_modals.edit.role_label')}</Label>
          <Select
            value={selectedRole}
            onValueChange={(value: UserRole) => setSelectedRole(value)}
          >
            <SelectTrigger className="w-full" id="edit-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </Modal>
  )
}

export default EditUserModal